import React, { useMemo, useEffect, useState, useCallback } from "react";
import SequenceFlowsDialog from "components/dialogs/SequenceFlowsDialog";
import { Container, Stack } from "@mui/material";
import { getTranslation, getStartedByMeProcessIds } from "common";
import MyActivitiesFuncBar from "../components/MyActivitiesFuncBar";
import MyActivitiesPanels from "../components/MyActivitiesPanels";
import MyActivitiesMobileView from "../components/myactivities-mobile/MyActivitiesMobileView";
import {
  useActivities,
  useSelectedOrganization,
  useTranslations,
  useUser,
  usePriorities,
  useIsMobile,
  useIsMobileLandscape,
} from "hooks";
import useUserDataKeys from "hooks/useUserDataKeys";
import { StartProcess, TaskDetailDialog } from "components/dialogs";
import { User } from "components/UserItem";
import {
  DragDropContext,
  OnDragEndResponder,
  OnDragUpdateResponder,
} from "react-beautiful-dnd";
import {
  CONSTANTS,
  filterTasks,
  processTasks,
  sortTasks,
} from "common/utilities";
import { organizationsApi, processesApi } from "api";
import { SequenceFlow, ServerTag, Task } from "model/activitiesModel";
import { SortState } from "hooks/useSortableList";
import { SortCriterion } from "common/utilities";
import { ProcessModelType } from "model/processModel";
import { Priority } from "hooks/usePriorities";
import useSubscriptionFeatures from "hooks/useSubscriptionFeatures";
import { useNavigate, useLocation, useParams } from "react-router-dom";
// Superficie SLA delle attivita' (F3c, piano sez. 3 riga "Attivita'" e sez. 5):
// innesto sulla pagina **esistente** (nessuna pagina nuova, decisione 2 sez. 14.2).
// Il provider da' a badge/dettaglio lo stato SLA senza una chiamata per riga; la
// strip rende banner (deadline reali) e preferenze per utente.
import SlaActivitiesProvider from "components/sla/slaActivitiesContext";
import SlaActivitiesSurface from "components/sla/SlaActivitiesSurface";
const LOCAL_STORAGE_KEY = `myActivitiesFilters`;
const DEFAULT_FILTERS = {
  searchText: "",
  searchTags: [] as number[],
  selectedPriorities: [] as string[],
  selectedModels: [] as number[],
  selectedActivities: [] as string[],
  selectedUserDataKey: null as string | null,
  sortCriteria: {} as SortCriterion,
  // "Creato da" filter: ids of users that started the processes whose
  // tasks the user wants to see. The current user is one of the options
  // in the picker (marked "(io)"). Other ids are stored for UI
  // consistency with Monitor/Storico, but only the current user is
  // matchable against the local `startedByMeIds` fallback until the BE
  // exposes `task.idstartuser`.
  selectedCreatorIds: [] as number[],
};
const getMyActivitiesStorageKey = (userId: number, organizationId: number) =>
  `${LOCAL_STORAGE_KEY}-${userId}-${organizationId}`;

const MyActivities = () => {
  const navigate = useNavigate();
  const { bypassSubscription, liveProcessCanUse, refreshSubscriptionFeatures } = useSubscriptionFeatures();
  const translations = useTranslations();
  const [initializedFiltersStorageKey, setInitializedFiltersStorageKey] = useState<string | null>(null);
  const organization = useSelectedOrganization();
  const [completeLabel, setCompleteLabel] = useState<boolean>(false);
  const [tasks, setTasks, orderedArray, loading] = useActivities(organization);
  const user = useUser();
  const isMobile = useIsMobile();
  const isMobileLandscape = useIsMobileLandscape();
  const filtersStorageKey = useMemo(
    () => organization?.id && user?.userId ? getMyActivitiesStorageKey(user.userId, organization.id) : null,
    [organization?.id, user?.userId]
  );
  const isInitialized = Boolean(filtersStorageKey && initializedFiltersStorageKey === filtersStorageKey);
  const [searchText, setSearchText] = useState<string>("");
  const [searchTags, setSearchTags] = useState<number[]>([]);
  const [tagList, setTagList] = useState<ServerTag[]>([]);
  const [all] = useState<boolean>(false);
  const [startProcess, setStartProcess] = useState(false);
  const [switchChecked] = useState(false);
  const [dragItem, setDragItem] = useState<Task | null>(null);
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);
  const [priorities] = usePriorities(organization?.id || null);
  const [selectedModels, setSelectedModels] = useState<number[]>([]);
  const [selectedActivities, setSelectedActivities] = useState<string[]>([]);
  const [selectedUserDataKey, setSelectedUserDataKey] = useState<string | null>(
    null
  );
  // "Creato da" filter: ids of users that started the processes whose
  // tasks the user wants to see. The current user is one of the options
  // in the picker (marked "(io)"). Other ids are stored for UI
  // consistency with Monitor/Storico, but only the current user is
  // matchable against the local `startedByMeIds` fallback until the BE
  // exposes `task.idstartuser`.
  const [selectedCreatorIds, setSelectedCreatorIds] = useState<number[]>([]);
  // Users of the current organization, loaded on demand to populate the
  // "Creato da" MultiSelect in the filter menu (desktop + mobile).
  const [orgUsers, setOrgUsers] = useState<User[]>([]);

  // Local fallback: ids of processes the current user has started in
  // this organization. TODO(BE): replace with `task.idstartuser` once
  // the activities stream exposes it.
  const startedByMeIds = useMemo(() => {
    if (!user?.userId || !organization?.id) return new Set<number>();
    return new Set(getStartedByMeProcessIds(user.userId, organization.id));
  }, [user?.userId, organization?.id, tasks]);

  const [userDataKeys] = useUserDataKeys(tasks);
  const selectedOrganization = useSelectedOrganization();
  const [processModels, setProcessModels] = useState<ProcessModelType[]>([]);
  const [sortCriteria, setSortCriteria] = useState<SortCriterion>({});
  const [showAllTags, setShowAllTags] = useState(false);
  const [showOnlyIdUserZero, setShowOnlyIdUserZero] = useState(false);
  const [openSequenceDialog, setOpenSequenceDialog] = useState(false);
  const [sequenceTask, setSequenceTask] = useState<Task | null>(null);
  const [view, setView] = useState<'minimal' | 'compact' | 'extended'>(
    () => (localStorage.getItem('taskCardView') as any) || 'extended'
  );

  const handleViewChange = (newView: 'minimal' | 'compact' | 'extended') => {
    localStorage.setItem('taskCardView', newView);
    setView(newView);
  };

  // Task in lista per il provider SLA: quando l'SSE porta token nuovi
  // (processo avviato, task arrivato) il provider rilegge le istanze SLA.
  const slaVisibleTasks = useMemo(
    () =>
      [...tasks.todo, ...tasks.doing].map((task) => ({
        idprocess: task.idprocess,
        designerid: task.designerid,
        idprocesstoken: task.idprocesstoken,
      })),
    [tasks.todo, tasks.doing]
  );

  const uniqueActivities = useMemo(() => {
    const allTasks = [...tasks.todo, ...tasks.doing];
    return Array.from(
      new Set(allTasks.map((task) => task.flownodename))
    ).filter((activity): activity is string => activity !== null);
  }, [tasks.todo, tasks.doing]);

  useEffect(() => {
    if (isInitialized && !loading && userDataKeys.length > 0) {
      setSelectedUserDataKey(prev => prev && userDataKeys.includes(prev) ? prev : null);
    }
  }, [userDataKeys, loading, isInitialized])

  //ci arriva da TaskDetailDialog
  const updateTaskPriority = useCallback(
    (taskId: number, newPriority: Priority) => {
      setTasks((prevTasks) => ({
        todo: prevTasks.todo.map((task) =>
          task.idprocesstoken === taskId
            ? { ...task, priority_name: newPriority.name }
            : task
        ),
        doing: prevTasks.doing.map((task) =>
          task.idprocesstoken === taskId
            ? { ...task, priority_name: newPriority.name }
            : task
        ),
      }));
    },
    [setTasks]
  ); //può essere vuoto?

  useEffect(() => {
    setCompleteLabel(
      JSON.parse(
        localStorage.getItem(CONSTANTS.STORE_LABEL_COMPLETE_MODE) || "false"
      )
    );
  }, []);


  // PRIMA: Leggi dal localStorage
  useEffect(() => {
    if (!organization?.id || !filtersStorageKey) {
      setInitializedFiltersStorageKey(null);
      return;
    }
    const savedFilters = localStorage.getItem(filtersStorageKey);
    const filters = savedFilters ? JSON.parse(savedFilters) : DEFAULT_FILTERS;
    setSearchText(filters.searchText || "");
    setSearchTags(filters.searchTags || []);
    setSelectedPriorities(filters.selectedPriorities || []);
    setSelectedModels(filters.selectedModels || []);
    setSelectedActivities(filters.selectedActivities || []);
    setSelectedUserDataKey(filters.selectedUserDataKey || null);
    setSortCriteria(filters.sortCriteria || {});
    setSelectedCreatorIds(Array.isArray(filters.selectedCreatorIds) ? filters.selectedCreatorIds : []);
    setInitializedFiltersStorageKey(filtersStorageKey); // <-- Marca come inizializzato DOPO aver letto
  }, [filtersStorageKey, organization?.id]);

  // POI: Salva nel localStorage SOLO dopo l'inizializzazione
  useEffect(() => {
    if (!filtersStorageKey || initializedFiltersStorageKey !== filtersStorageKey) return; // <-- Non salvare se non ancora inizializzato

    const filters = {
      searchText,
      searchTags,
      selectedPriorities,
      selectedActivities,
      selectedModels,
      selectedUserDataKey,
      sortCriteria,
      selectedCreatorIds,
    };
    localStorage.setItem(filtersStorageKey, JSON.stringify(filters));
  }, [
    filtersStorageKey,
    initializedFiltersStorageKey, // <-- Aggiungi alle dipendenze
    sortCriteria,
    searchText,
    searchTags,
    selectedPriorities,
    selectedActivities,
    selectedModels,
    selectedUserDataKey,
    selectedCreatorIds,
  ]);

  const handleSelectUserDataKey = (selectedKey: string | null) => {
    setSelectedUserDataKey(selectedKey);
    if (!selectedKey) {
      setSortCriteria((prev) => ({
        ...prev,
        userdata: SortState.None,
      }));
    }
  };

  const SUPPORTED_SORTS = [
    {
      field: "priority",
      label: (userDataKey: string | null) =>
        getTranslation(translations, "generic.sort.by.priority"),
    },
    {
      field: "name",
      label: (userDataKey: string | null) =>
        getTranslation(translations, "generic.sort.by.process_name"),
    },
    {
      field: "startDate",
      label: (userDataKey: string | null) =>
        getTranslation(translations, "generic.sort.by.startdate"),
    },
    {
      checkDisabled: function (userDataKey: string | null) {
        return !Boolean(userDataKey);
      },
      field: "userdata",
      label: (userDataKey: string | null) =>
        `${getTranslation(translations, "generic.sort.by.userdatakey")} ${userDataKey || ""
        }`,
    },
  ];

  useEffect(() => {
    if (selectedOrganization?.id) {
      organizationsApi
        .get(`/${selectedOrganization.id}/processmodels/v2`)
        .then((res) => {
          setProcessModels(
            (res.data.processmodels as ProcessModelType[]).filter(
              (a) => a.modeltype
            )
          );
        })
        .catch((error) => { });
    }
  }, [selectedOrganization]);


  useEffect(() => {
    if (organization?.id) {
      (async () => {
        try {
          const { data } = await organizationsApi.get<{
            tag: { name: string; color: string; id: number }[];
          }>(`${organization.id}/tags`);
          setTagList(
            data.tag.map((tag) => ({
              color: tag.color,
              idtag: tag.id,
              tag: tag.name,
            }))
          );
        } catch {
        } finally {
        }
      })();
    }
  }, [organization?.id]);

  // Fetch org users for the "Creato da" filter MultiSelect. Mirrors the
  // pattern used by ProcessStarted (src/components/ProcessStarted.tsx) so
  // the same UI is consistent across Monitor/Storico/MyActivities.
  useEffect(() => {
    if (!organization?.id) {
      setOrgUsers([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await organizationsApi.get(`${organization.id}/users`);
        if (!cancelled && Array.isArray(data.users)) {
          setOrgUsers(data.users);
        } else if (!cancelled) {
          setOrgUsers([]);
        }
      } catch {
        if (!cancelled) setOrgUsers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organization?.id]);

  const orderArray = useCallback(
    (
      source: "todo" | "doing",
      dest: "todo" | "doing",
      taskId: number,
      destIdx: number | null = null
    ) => {
      setTasks((prevTasks) => {
        const _tasks = {
          todo: [...prevTasks.todo],
          doing: [...prevTasks.doing],
        };

        const sourceIdx = _tasks[source].findIndex(
          (task) => task.idprocesstoken === taskId
        );
        if (sourceIdx === -1) {
          return _tasks;
        }

        const [task] = _tasks[source].splice(sourceIdx, 1);

        if (destIdx !== null) {
          task.state = source === "todo" ? 1 : 0;

          _tasks[dest].splice(destIdx, 0, { ...task, loading: true });
        }

        orderedArray.current = {
          todo: _tasks.todo.map((_) => _.idprocesstoken),
          doing: _tasks.doing.map((_) => _.idprocesstoken),
        };

        return _tasks;
      });
    },
    [orderedArray, setTasks]
  );


  /**
   * Spegne il flag ottimistico `loading` messo da `setLoadingToTask` / da
   * `orderArray` nel drag&drop. Cerca in ENTRAMBE le colonne: nel drag la card
   * e' gia' stata spostata in quella di destinazione, quindi il `state` del task
   * non basta a dire dove sia finita.
   *
   * Sta nel `finally` di OGNI transizione: l'unico altro a spegnerlo era il
   * reload spinto dall'SSE, quindi se l'evento non arriva (o il refetch fallisce)
   * la card resta col Backdrop/CircularProgress a girare per sempre.
   */
  const clearLoadingToTask = (task: Task) => {
    setTasks((__tasks) => {
      let changed = false;
      const clear = (list: Task[]) =>
        list.map((t) => {
          if (t.idprocesstoken !== task.idprocesstoken || !t.loading) return t;
          changed = true;
          return { ...t, loading: false };
        });
      const todo = clear(__tasks.todo);
      const doing = clear(__tasks.doing);
      return changed ? { todo, doing } : __tasks;
    });
  };

  const fromTodoTo_Doing = async (task: Task) => {
    try {
      await processesApi.post(`/${task.idprocesstoken}/nextstate/doing`);
    } catch {
    } finally {
      clearLoadingToTask(task);
    }
  };
  const fromDoingTo_Done = async (task: Task) => {
    try {
      await processesApi.post(`/${task.idprocesstoken}/nextstate/done`);
    } catch {
    } finally {
      clearLoadingToTask(task);
    }
  };
  const fromDoingTo_Todo = async (task: Task) => {
    try {
      await processesApi.post(`/${task.idprocesstoken}/undo`);
    } catch {
    } finally {
      clearLoadingToTask(task);
    }
  };
  const fromDoingTo_Sequence = async (
    task: Task,
    sequenceFlow: SequenceFlow
  ) => {
    try {
      await processesApi.post(
        `tokens/${task.idprocesstoken}/exit/${sequenceFlow.designerid}`
      );
    } catch {
    } finally {
      clearLoadingToTask(task);
    }
  };

  const filteredTags = useMemo(() => {
    return tagList.filter((tag) =>
      tasks.todo
        .concat(tasks.doing)
        .some((task) => task.jsontag?.includes(tag.idtag as unknown as string))
    );
  }, [tagList, tasks.todo, tasks.doing]);

  const handleDragUpdate: OnDragUpdateResponder = ({ destination }) => {
    setDragItem((task) =>
      task
        ? {
            ...task,
            isOverDone: destination?.droppableId === "done",
          }
        : null
    );
  };
  const handleDragEnd: OnDragEndResponder = async ({
    reason,
    source,
    destination,
    draggableId,
  }) => {
    if (destination && reason !== "CANCEL") {
      const sourceTasks = source.droppableId === "todo" ? processedToDoTasks : processedDoingTasks;
      const task = sourceTasks.find(t => t.idprocesstoken.toString() === draggableId);
      if (!task) return;

      if (source.droppableId === "doing") {
        if (destination.droppableId === "done") {
          if (task.sequenceflows && task.sequenceflows.length > 1) {
            setSequenceTask(task);
            setOpenSequenceDialog(true);
            return;
          }
          orderArray("doing", "todo", task.idprocesstoken);
          if (task.sequenceflows?.length === 1) {
            await fromDoingTo_Sequence(task, task.sequenceflows[0]);
          } else {
            await fromDoingTo_Done(task);
          }
        } else {
          orderArray("doing", "todo", task.idprocesstoken, destination.index);
          await fromDoingTo_Todo(task);
        }
      } else {
        orderArray("todo", "doing", task.idprocesstoken, destination.index);
        await fromTodoTo_Doing(task);
      }
    }
    setDragItem(null);
  };

  const [detail, setDetail] = useState<number | null>(null);
  const location = useLocation();
  // Deep-link notifiche SLA (decisione 2, sez. 14.2): la rotta
  // `/dashboard/myactivities/task/:taskId` riusa questa pagina e seleziona
  // l'attivita' dal parametro. Lo `state.taskId` resta come fallback per la
  // navigazione programmatica gia' esistente.
  const { taskId: taskIdParam } = useParams<{ taskId: string }>();

  useEffect(() => {
    const taskId =
      Number(taskIdParam) || (location.state as { taskId?: number })?.taskId;
    if (taskId) {
      setDetail(taskId);
    }
  }, [taskIdParam, location.state]);

  // Effective set of creator ids the filter is checking against.
  // NOTE: without `task.idstartuser` from the BE, only the current user
  // is matchable via the local `startedByMeIds` fallback. Selecting any
  // other user is preserved in the UI for consistency with the
  // Monitor/Storico filters but yields zero matches until the BE exposes
  // the creator on Task.
  const effectiveCreatorIds = useMemo(
    () => new Set<number>(selectedCreatorIds),
    [selectedCreatorIds],
  );

  const matchesCreator = useCallback(
    (idprocess: number) => {
      if (effectiveCreatorIds.size === 0) return true;
      // Current user is the only id we can match against (local fallback).
      if (user?.userId && effectiveCreatorIds.has(user.userId)) {
        return startedByMeIds.has(idprocess);
      }
      // Other creators — no BE support yet, no match.
      return false;
    },
    [effectiveCreatorIds, user?.userId, startedByMeIds]
  );

  const processedToDoTasks = useMemo(
    () =>
      processTasks(
        tasks.todo,
        searchText,
        searchTags,
        selectedPriorities,
        selectedActivities,
        selectedUserDataKey,
        selectedModels,
        sortCriteria,
        priorities
      )
        //commented atm
        // .filter(
        //   ({ idprocesstoken }) => idprocesstoken !== dragItem?.idprocesstoken
        // )
        .filter((task) => !showOnlyIdUserZero || task.iduser === 0)
        .filter(
          (task) =>
            selectedActivities.length === 0 ||
            selectedActivities.includes(task?.flownodename ?? "")
        )
        // "Creati da" filter (single- or multi-user). The task passes only
        // if it matches via the local fallback (only the current user is
        // matchable until the BE exposes `task.idstartuser`).
        .filter((task) => matchesCreator(task.idprocess)),
    [
      //dragitem dependency is related to the commented filter
      // dragItem,
      tasks.todo,
      searchText,
      searchTags,
      selectedPriorities,
      selectedUserDataKey,
      selectedModels,
      sortCriteria,
      priorities,
      selectedActivities,
      showOnlyIdUserZero,
      matchesCreator,
    ]
  );
  const processedDoingTasks = useMemo(
    () =>
      processTasks(
        tasks.doing,
        searchText,
        searchTags,
        selectedPriorities,
        selectedActivities,
        selectedUserDataKey,
        selectedModels,
        sortCriteria,
        priorities
      )
        //commentend atm
        // .filter(
        //   ({ idprocesstoken }) => idprocesstoken !== dragItem?.idprocesstoken
        // )
        .filter(
          (task) =>
            selectedActivities.length === 0 ||
            selectedActivities.includes(task?.flownodename ?? "")
        )
        .filter((task) => matchesCreator(task.idprocess)),
    [
      //dragitem dependency is related to the commented filter
      // dragItem,
      tasks.doing,
      searchText,
      searchTags,
      selectedPriorities,
      selectedUserDataKey,
      selectedModels,
      sortCriteria,
      priorities,
      selectedActivities,
      matchesCreator,
      //   showOnlyIdUserZero
    ]
  );
  const appliedFilter = useMemo(() => {
    return (
      searchTags.length +
      (searchText ? 1 : 0) +
      selectedModels.length +
      selectedPriorities.length +
      selectedActivities.length +
      selectedCreatorIds.length
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedUserDataKey,
    searchTags,
    searchText,
    selectedModels,
    selectedPriorities,
    selectedActivities,
    selectedCreatorIds,
  ]);

  const displayedTags = useMemo(() => {
    return showAllTags ? filteredTags : filteredTags.slice(0, 5);
  }, [filteredTags, showAllTags]);

  const selectedTask = useMemo(() => {
    return (
      tasks.todo.find((_) => _.idprocesstoken === detail) ||
      tasks.doing.find((_) => _.idprocesstoken === detail)
    );
  }, [tasks, detail]);

  const handleClearFilters = useCallback(
    (disableLocalStorage = false, clearUserData = false) => {
      setSearchText("");
      setSearchTags([]);
      setSelectedPriorities([]);
      setSelectedModels([]);
      if (clearUserData) {
        setSelectedUserDataKey(null);
      }
      setSelectedActivities([]);
      setSelectedCreatorIds([]);
      if (!disableLocalStorage) {
        if (filtersStorageKey) {
          localStorage.removeItem(filtersStorageKey);
        }
      }
    },
    [filtersStorageKey, selectedUserDataKey, sortCriteria]
  );

  //anche questo sarà da rivedere per usarne solo uno (vedi ProcessStarted)
  const setLoadingToTask = (task: Task) => {
    setTasks((__tasks) => {
      const _tasks = { ...__tasks };
      const container = task.state === 0 ? "todo" : "doing";
      // const sorted = sortTasks(
      //   _tasks[container].filter(
      //     filterTasks(
      //       searchText,
      //       searchTags,
      //       selectedPriorities,
      //       selectedActivities,
      //       selectedUserDataKey,
      //       selectedModels
      //     )
      //   ),
      //   sortCriteria,
      //   selectedUserDataKey,
      //   priorities
      // );
      const selectedIndex = _tasks[container].findIndex(
        (a) => a.idprocesstoken === task.idprocesstoken
      );
      if (selectedIndex !== -1) {
        _tasks[container][selectedIndex] = {
          ..._tasks[container][selectedIndex],
          loading: true,
        };
      }
      // _tasks[container] = sorted;
      return _tasks;
    });
  };

  if (organization && organization.id && user) {
    // Mobile-first rendering (portrait + landscape).
    // The drag&drop flow is bypassed entirely on mobile: tap a card to open the
    // detail drawer/dialog and use the actions there. The full desktop layout
    // (Kanban panels + DragDropContext) is preserved below.
    if (isMobile || isMobileLandscape) {
      return (
        <SlaActivitiesProvider idorganization={organization.id} tasks={slaVisibleTasks}>
          {/* Strip SLA (F3c): banner su deadline reali del payload e preferenze
              per utente. Su mobile e' la stessa strip, in cima alla pagina. */}
          <SlaActivitiesSurface />
          <TaskDetailDialog
            doing={selectedTask?.state === 1}
            task={selectedTask}
            open={Boolean(selectedTask)}
            onClose={() => setDetail(null)}
            handleNextStep={(task) => {
              setLoadingToTask(task);
              return selectedTask?.state === 1
                ? fromDoingTo_Done(task)
                : fromTodoTo_Doing(task);
            }}
            handleSequenceFlowChoice={(task, sequenceFlow) => {
              setLoadingToTask(task);
              return selectedTask?.state === 1
                ? fromDoingTo_Sequence(task, sequenceFlow)
                : undefined;
            }}
            handleBackStep={(task) => {
              setLoadingToTask(task);
              return selectedTask?.state === 1
                ? fromDoingTo_Todo(task)
                : undefined;
            }}
            administrator={organization.administrator}
            onPriorityUpdate={updateTaskPriority}
          />
          <StartProcess
            isAdmin={organization.administrator}
            open={startProcess}
            setOpen={setStartProcess}
            priorities={priorities}
            onProcessStarted={refreshSubscriptionFeatures}
          />
          <MyActivitiesMobileView
            todoTasks={processedToDoTasks}
            doingTasks={processedDoingTasks}
            searchText={searchText}
            setSearchText={setSearchText}
            searchTags={searchTags}
            setSearchTags={setSearchTags}
            selectedPriorities={selectedPriorities}
            setSelectedPriorities={setSelectedPriorities}
            selectedModels={selectedModels}
            setSelectedModels={setSelectedModels}
            selectedUserDataKey={selectedUserDataKey}
            handleSelectUserDataKey={handleSelectUserDataKey}
            setSortCriteria={setSortCriteria}
            handleClearFilters={handleClearFilters}
            processModels={processModels}
            tagList={tagList}
            priorities={priorities}
            userDataKeys={userDataKeys}
            setStartProcess={setStartProcess}
            setDetail={setDetail}
            liveProcessCanUse={Boolean(liveProcessCanUse)}
            bypassSubscription={Boolean(bypassSubscription)}
            organizationId={organization.id}
            organizationName={organization.name}
            isLandscape={isMobileLandscape}
            translations={translations}
            selectedCreatorIds={selectedCreatorIds}
            setSelectedCreatorIds={setSelectedCreatorIds}
            orgUsers={orgUsers}
            onNextStep={async (task) => {
              setLoadingToTask(task);
              try {
                if (task.state === 1) {
                  if (task.sequenceflows && task.sequenceflows.length > 1) {
                    setSequenceTask(task);
                    setOpenSequenceDialog(true);
                    return;
                  }
                  if (task.sequenceflows?.length === 1) {
                    await fromDoingTo_Sequence(task, task.sequenceflows[0]);
                    return;
                  }
                  await fromDoingTo_Done(task);
                } else {
                  await fromTodoTo_Doing(task);
                }
              } finally {
                // The SSE stream normally refreshes the list and the task
                // disappears (or moves) within a second. If the network
                // hiccups, however, the task stays put with `loading = true`
                // and the in-card next/back buttons are forever disabled.
                // Resetting loading here guarantees the buttons re-enable
                // even when the SSE push is delayed.
                clearLoadingToTask(task);
              }
            }}
            onBackStep={async (task) => {
              if (task.state !== 1) return;
              setLoadingToTask(task);
              try {
                await fromDoingTo_Todo(task);
              } finally {
                clearLoadingToTask(task);
              }
            }}
          />
        </SlaActivitiesProvider>
      );
    }

    return (
      <SlaActivitiesProvider idorganization={organization.id} tasks={slaVisibleTasks}>
        {/* Strip SLA (F3c): banner su deadline reali del payload e preferenze
            per utente, sopra i pannelli ToDo/Doing esistenti. */}
        <SlaActivitiesSurface />
        <TaskDetailDialog
          doing={selectedTask?.state === 1}
          task={selectedTask}
          open={Boolean(selectedTask)}
          onClose={() => setDetail(null)}
          handleNextStep={(task) => {
            setLoadingToTask(task);
            return selectedTask?.state === 1
              ? fromDoingTo_Done(task)
              : fromTodoTo_Doing(task);
          }}
          handleSequenceFlowChoice={(task, sequenceFlow) => {
            setLoadingToTask(task);
            return selectedTask?.state === 1
              ? fromDoingTo_Sequence(task, sequenceFlow)
              : undefined;
          }}
          handleBackStep={(task) => {
            setLoadingToTask(task);
            return selectedTask?.state === 1
              ? fromDoingTo_Todo(task)
              : undefined;
          }}
          administrator={organization.administrator}
          onPriorityUpdate={updateTaskPriority}
        />

        <StartProcess
          isAdmin={organization.administrator}
          open={startProcess}
          setOpen={setStartProcess}
          priorities={priorities}
          onProcessStarted={refreshSubscriptionFeatures}
        />
        <DragDropContext
          onDragUpdate={handleDragUpdate}
          onDragEnd={handleDragEnd}
          onBeforeDragStart={(e) => {
            const sorted = sortTasks(
              tasks[e.source.droppableId as "todo" | "doing"].filter(
                filterTasks(
                  searchText,
                  searchTags,
                  selectedPriorities,
                  selectedActivities,
                  selectedUserDataKey,
                  selectedModels
                )
              ),
              sortCriteria,
              selectedUserDataKey,
              priorities
            );
            const selected = sorted[e.source.index];
            setDragItem(selected);
          }}
        >
          <Stack sx={{ overflowX: "auto" }} height="100%" spacing={1}>
            <Stack
              sx={{ overflow: "hidden", width: 'fit-content' }}
              overflow="auto"
              flex={1}
              spacing={2}
            >
              <MyActivitiesFuncBar
                liveProcessCanUse={liveProcessCanUse}
                bypassSubscription={bypassSubscription}
                organization={organization}
                translations={translations}
                view={view}
                completeLabel={completeLabel}
                appliedFilter={appliedFilter}
                sortCriteria={sortCriteria}
                setStartProcess={setStartProcess}
                navigate={navigate}
                handleViewChange={handleViewChange}
                setCompleteLabel={setCompleteLabel}
                searchText={searchText}
                setSearchText={setSearchText}
                searchTags={searchTags}
                setSearchTags={setSearchTags}
                selectedPriorities={selectedPriorities}
                setSelectedPriorities={setSelectedPriorities}
                selectedModels={selectedModels}
                setSelectedModels={setSelectedModels}
                selectedActivities={selectedActivities}
                setSelectedActivities={setSelectedActivities}
                selectedUserDataKey={selectedUserDataKey}
                handleSelectUserDataKey={handleSelectUserDataKey}
                setSortCriteria={setSortCriteria}
                handleClearFilters={handleClearFilters}
                processModels={processModels}
                tasks={tasks}
                uniqueActivities={uniqueActivities}
                displayedTags={displayedTags}
                filteredTags={filteredTags}
                showAllTags={showAllTags}
                setShowAllTags={setShowAllTags}
                priorities={priorities}
                userDataKeys={userDataKeys}
                SUPPORTED_SORTS={SUPPORTED_SORTS}
                selectedCreatorIds={selectedCreatorIds}
                setSelectedCreatorIds={setSelectedCreatorIds}
                orgUsers={orgUsers}
              />
              <MyActivitiesPanels
                loading={loading}
                completeLabel={completeLabel}
                setDetail={setDetail}
                fromTodoTo_Doing={(task) => {
                  setLoadingToTask(task);
                  return fromTodoTo_Doing(task);
                }}
                dragItem={dragItem}
                userId={user.userId}
                switchChecked={switchChecked}
                administrator={!!organization.administrator}
                processedToDoTasks={processedToDoTasks}
                showOnlyIdUserZero={showOnlyIdUserZero}
                setShowOnlyIdUserZero={setShowOnlyIdUserZero}
                view={view}
                fromDoingTo_Done={(task) => {
                  if (task.sequenceflows && task.sequenceflows.length > 1) {
                    setSequenceTask(task);
                    setOpenSequenceDialog(true);
                    return Promise.resolve();
                  }
                  setLoadingToTask(task);
                  if (task.sequenceflows?.length === 1) {
                    return fromDoingTo_Sequence(task, task.sequenceflows[0]);
                  }
                  return fromDoingTo_Done(task);
                }}
                fromDoingTo_Todo={(task) => {
                  setLoadingToTask(task);
                  return fromDoingTo_Todo(task);
                }}
                fromDoingTo_Sequence={(task, sequenceFlow) => {
                  setLoadingToTask(task);
                  return fromDoingTo_Sequence(task, sequenceFlow);
                }}
                processedDoingTasks={processedDoingTasks}
                all={all}
              />
            </Stack>
          </Stack>
        </DragDropContext>
        {sequenceTask && (
          <SequenceFlowsDialog
            gatewayQuestion={sequenceTask?.flownodename}
            open={openSequenceDialog}
            onClose={() => {
              setOpenSequenceDialog(false);
              setSequenceTask(null);
              setDragItem(null);
            }}
            sequenceFlows={sequenceTask?.sequenceflows ?? []}
            onSelect={(sequenceFlow: SequenceFlow) => {
              if (sequenceTask) {
                setLoadingToTask(sequenceTask);
                fromDoingTo_Sequence(sequenceTask, sequenceFlow);
                setOpenSequenceDialog(false);
                setSequenceTask(null);
                setDragItem(null);
              }
            }}
          />
        )}
      </SlaActivitiesProvider>
    );
  } else {
    return (
      <Container
        sx={{
          display: "flex",
          JustifyContent: "center",
          alignItems: "center",
        }}
      >
        {getTranslation(translations, "placeholders.organization.select.label")}
      </Container>
    );
  }
};

export default MyActivities;
